import { EventPoster } from '@/features/events/shared/EventPoster';
import { BusinessDealsPanel } from '@/features/venues/deals/BusinessDealsPanel';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/async-state';
import { Icon } from '@/components/ui/icon';
import { MetaBadge, AppScreen } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { BusinessShellNav } from './BusinessShellNav';
import { WorkspaceAccessState } from './WorkspaceAccessState';
import { useAuth } from '@/lib/auth/auth';
import { getAPIErrorRecoveryCopy } from '@/lib/api/apiErrorCopy';
import { useBusinessWorkspace } from './useBusinessWorkspace';
import type { OrgActivityEventType, OrgActivityItem } from '@/lib/api/apiTypes';
import { useOrganizationActivity } from './useOrganizationActivity';
import { useVenueAnalyticsOverview } from '@/features/venues/useVenueAnalyticsOverview';
import { useVenueEvents } from '@/features/venues/useVenueEvents';
import { trackBusinessActionTapped } from '@/lib/analytics/posthog';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/features/events/eventFormatting';
import {
  formatWorkspaceEventTime,
  formatWorkspaceMetric,
  getBusinessShellHref,
} from './workspace';
import { Redirect, router } from 'expo-router';
import * as React from 'react';
import {
  ArrowRightIcon,
  BadgeCheckIcon,
  CalendarDaysIcon,
  Building2Icon,
  CalendarPlusIcon,
  EyeIcon,
  HistoryIcon,
  LockKeyholeIcon,
  PencilIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserCogIcon,
  UserMinusIcon,
  UserPlusIcon,
} from 'lucide-react-native';
import { Pressable, ScrollView, View } from 'react-native';

export function WorkspaceHomeScene({
  organizationID,
  showSectionNav = true,
}: {
  organizationID: string;
  showSectionNav?: boolean;
}) {
  const session = useAuth((state) => state.session);
  const { organizationQuery, activeVenue, canManageVenues, canViewAnalytics, orgAccessEnabled } =
    useBusinessWorkspace(organizationID);
  const venueAnalyticsQuery = useVenueAnalyticsOverview(
    activeVenue?.venue_id ?? null,
    canViewAnalytics
  );
  const venueEventsQuery = useVenueEvents(activeVenue?.venue_id ?? null);
  const canViewActivity =
    organizationQuery.data?.viewer_role === 'owner' ||
    organizationQuery.data?.viewer_role === 'manager';
  const [selectedActivityVenueID, setSelectedActivityVenueID] = React.useState<string | null>(
    activeVenue?.venue_id ?? null
  );
  const showActivityVenueFilter = (organizationQuery.data?.venues.length ?? 0) > 1;

  React.useEffect(() => {
    setSelectedActivityVenueID(activeVenue?.venue_id ?? null);
  }, [activeVenue?.venue_id]);

  const activityQuery = useOrganizationActivity(
    organizationID,
    selectedActivityVenueID,
    canViewActivity
  );
  const trackHomeAction = React.useCallback(
    (action: Parameters<typeof trackBusinessActionTapped>[0]['action'], eventID?: string | null) => {
      trackBusinessActionTapped({
        action,
        eventID,
        organizationID,
        source: 'business_home',
        venueID: activeVenue?.venue_id ?? null,
      });
    },
    [activeVenue?.venue_id, organizationID]
  );

  if (!session) {
    return (
      <Redirect
        href={{ pathname: '/sign-in', params: { next: `/workspaces/${organizationID}` } }}
      />
    );
  }

  if (!orgAccessEnabled) {
    return <Redirect href={'/workspaces' as never} />;
  }

  const events = venueEventsQuery.data?.events ?? [];
  const nextEvent = events[0];
  const analyticsTotals = venueAnalyticsQuery.data?.totals;
  const reachTotal = analyticsTotals
    ? analyticsTotals.event_impressions +
      analyticsTotals.deal_impressions +
      analyticsTotals.map_appearances +
      analyticsTotals.search_appearances
    : 0;
  const viewTotal = analyticsTotals
    ? analyticsTotals.profile_views + analyticsTotals.event_views + analyticsTotals.deal_views
    : 0;
  const actionTotal = analyticsTotals
    ? analyticsTotals.rsvps +
      analyticsTotals.direction_taps +
      analyticsTotals.shares +
      analyticsTotals.comments
    : 0;
  const overviewMetrics =
    canViewAnalytics && analyticsTotals
      ? [
          { label: 'Reach', value: formatWorkspaceMetric(reachTotal) },
          { label: 'Views', value: formatWorkspaceMetric(viewTotal) },
          { label: 'Actions', value: formatWorkspaceMetric(actionTotal) },
          ...(organizationQuery.data?.viewer_role !== 'social'
            ? [{ label: 'Followers', value: formatWorkspaceMetric(analyticsTotals.followers) }]
            : []),
        ]
      : [
          { label: 'Events', value: formatWorkspaceMetric(events.length) },
          {
            label: 'RSVPs',
            value: formatWorkspaceMetric(
              events.reduce((sum, event) => sum + event.rsvp_count, 0)
            ),
          },
        ];
  return (
    <AppScreen>
      <ScrollView
        className="flex-1"
        contentContainerStyle={{ paddingBottom: 40 }}
        showsVerticalScrollIndicator={false}>
        <View style={{ paddingHorizontal: 16, paddingTop: 10 }}>
          {organizationQuery.isPending ? <LoadingState label="Loading venue..." /> : null}
          {organizationQuery.isError ? (
            <ErrorState
              body={getAPIErrorRecoveryCopy(
                organizationQuery.error,
                'workspace',
                'Unable to load venue.'
              )}
              onAction={() => organizationQuery.refetch()}
            />
          ) : null}
        </View>

        {organizationQuery.data ? (
          <View className="gap-0">
            <View style={{ paddingHorizontal: 16, paddingTop: 10 }}>
              <BusinessShellNav
                organizationID={organizationID}
                organizationName={organizationQuery.data.name}
                viewerRole={organizationQuery.data.viewer_role}
                activeTab="home"
                activeVenue={activeVenue}
                showSectionNav={showSectionNav}
              />
            </View>

            {!activeVenue ? (
              <View style={{ paddingHorizontal: 16, paddingTop: 24 }}>
                <WorkspaceAccessState
                  icon={
                    organizationQuery.data.viewer_role === 'owner'
                      ? Building2Icon
                      : LockKeyholeIcon
                  }
                  title={
                    organizationQuery.data.viewer_role === 'owner'
                      ? 'No venue yet'
                      : 'No venue access yet'
                  }
                  body={
                    organizationQuery.data.viewer_role === 'owner'
                      ? 'Claim your first venue or finish setup in Backstage to unlock the business dashboard.'
                      : 'This organization has not assigned you a venue yet. Ask an owner or manager to give you venue access.'
                  }
                />
              </View>
            ) : (
              <>
                {/* ── Stats band — edge to edge, no card ─────────── */}
                <View className="border-b border-border/60">
                  {canViewAnalytics && venueAnalyticsQuery.isPending ? (
                    <View className="py-5 items-center">
                      <Text variant="small" className="text-muted-foreground">Loading stats…</Text>
                    </View>
                  ) : (
                    <View className="flex-row">
                      {overviewMetrics.map((metric, index) => (
                        <View
                          key={metric.label}
                          className={cn(
                            'flex-1 items-center py-4',
                            index > 0 && 'border-l border-border/60'
                          )}>
                          <Text className="text-[22px] font-bold leading-none text-foreground">
                            {metric.value}
                          </Text>
                          <Text variant="small" className="mt-1.5 text-muted-foreground">
                            {metric.label}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}
                </View>

                {/* ── Next up ──────────────────────────────────────── */}
                <View style={{ paddingHorizontal: 16, paddingTop: 24 }} className="gap-3">
                  <Eyebrow>Next up</Eyebrow>
                  {venueEventsQuery.isPending ? (
                    <LoadingState label="Loading schedule…" />
                  ) : venueEventsQuery.isError ? (
                    <ErrorState
                      body={getAPIErrorRecoveryCopy(
                        venueEventsQuery.error,
                        'venue',
                        'Unable to load events.'
                      )}
                      onAction={() => venueEventsQuery.refetch()}
                    />
                  ) : nextEvent ? (
                    <FeaturedEventRow event={nextEvent} />
                  ) : (
                    <View className="py-6">
                      <EmptyState title="No events scheduled" body="Create the first event for this venue." />
                    </View>
                  )}
                </View>

                {/* ── Actions — flat rows on canvas ────────────────── */}
                <View style={{ paddingHorizontal: 16, paddingTop: 28, paddingBottom: 12 }}>
                  <Eyebrow>Manage</Eyebrow>
                </View>
                <View className="border-t border-border/60">
                  <ActionRow
                    icon={CalendarDaysIcon}
                    label="Event operations"
                    onPress={() => {
                      trackHomeAction('event_operations');
                      router.push(
                        showSectionNav
                          ? (`/workspaces/${organizationID}/events` as never)
                          : (getBusinessShellHref('events') as never)
                      );
                    }}
                  />
                  {canManageVenues ? (
                    <>
                      <ActionRow
                        icon={CalendarPlusIcon}
                        label="Create event"
                        onPress={() => {
                          trackHomeAction('create_event');
                          router.push({
                            pathname: '/events/create',
                            params: {
                              venueId: activeVenue.venue_id,
                              venueName: activeVenue.venue_name,
                            },
                          });
                        }}
                      />
                      <ActionRow
                        icon={PencilIcon}
                        label="Edit venue"
                        onPress={() => {
                          trackHomeAction('edit_venue');
                          router.push(`/venues/${activeVenue.venue_id}/edit` as never);
                        }}
                      />
                    </>
                  ) : null}
                  <ActionRow
                    icon={EyeIcon}
                    label="Live page"
                    onPress={() => {
                      trackHomeAction('view_live_page');
                      router.push(`/venues/${activeVenue.venue_id}` as never);
                    }}
                  />
                </View>

                {/* ── Deals ────────────────────────────────────────── */}
                {canManageVenues ? (
                  <View style={{ paddingHorizontal: 16, paddingTop: 28 }}>
                    <BusinessDealsPanel
                      canManage={canManageVenues}
                      venueID={activeVenue.venue_id}
                    />
                  </View>
                ) : null}

                {/* ── Activity log ─────────────────────────────────── */}
                {canViewActivity ? (
                  <View style={{ paddingHorizontal: 16, paddingTop: 28 }} className="gap-3">
                    <Eyebrow>Activity</Eyebrow>
                    {showActivityVenueFilter ? (
                      <View className="flex-row flex-wrap gap-2">
                        <ActivityFilterChip
                          active={selectedActivityVenueID == null}
                          onPress={() => setSelectedActivityVenueID(null)}>
                          All venues
                        </ActivityFilterChip>
                        {organizationQuery.data?.venues.map((venue) => (
                          <ActivityFilterChip
                            key={venue.venue_id}
                            active={selectedActivityVenueID === venue.venue_id}
                            onPress={() => setSelectedActivityVenueID(venue.venue_id)}>
                            {venue.venue_name}
                          </ActivityFilterChip>
                        ))}
                      </View>
                    ) : null}
                    {activityQuery.isPending ? (
                      <LoadingState label="Loading activity…" />
                    ) : activityQuery.isError ? (
                      <ErrorState
                        body="Unable to load activity."
                        onAction={() => activityQuery.refetch()}
                      />
                    ) : (activityQuery.data?.activity.length ?? 0) > 0 ? (
                      <View>
                        {activityQuery.data?.activity.slice(0, 5).map((item, index) => (
                          <ActivityRow
                            key={item.id}
                            item={item}
                            first={index === 0}
                          />
                        ))}
                      </View>
                    ) : (
                      <View className="items-center py-6">
                        <Icon as={HistoryIcon} size={20} className="text-muted-foreground/40" />
                        <Text
                          variant="small"
                          className="mt-3 max-w-[240px] text-center leading-5 text-muted-foreground/60">
                          Team updates and setup milestones will appear here.
                        </Text>
                      </View>
                    )}
                  </View>
                ) : null}
              </>
            )}
          </View>
        ) : null}
      </ScrollView>
    </AppScreen>
  );
}

// ── Private components ────────────────────────────────────────────────────────

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Text className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
      {children}
    </Text>
  );
}

function ActionRow({
  icon,
  label,
  onPress,
}: {
  icon: React.ComponentProps<typeof Icon>['as'];
  label: string;
  onPress: () => void;
}) {
  return (
    <Pressable
      className="flex-row items-center gap-3.5 border-b border-border/40 px-4 py-3 active:bg-surface-alt/50"
      onPress={onPress}>
      <Icon as={icon} size={16} className="text-primary" />
      <Text className="flex-1 text-[15px] font-medium text-foreground">{label}</Text>
      <Icon as={ArrowRightIcon} size={13} className="text-muted-foreground/40" />
    </Pressable>
  );
}

function FeaturedEventRow({
  event,
}: {
  event: NonNullable<ReturnType<typeof useVenueEvents>['data']>['events'][number];
}) {
  return (
    <Pressable
      className="flex-row items-stretch gap-3.5 rounded-xl border border-border bg-card p-3.5 active:opacity-80"
      onPress={() => router.push(`/event/${event.id}` as never)}>
      <EventPoster event={event} className="h-[90px] w-[68px] rounded-lg" showMeta={false} />
      <View className="flex-1 justify-between py-0.5">
        <View className="gap-1">
          <Text
            className="text-[15px] font-semibold leading-[21px] text-foreground"
            numberOfLines={2}>
            {event.title}
          </Text>
          {event.is_cancelled ? (
            <MetaBadge label="Cancelled" />
          ) : (
            <Text variant="small" className="font-medium text-muted-foreground">
              {formatWorkspaceEventTime(event.starts_at)}
            </Text>
          )}
        </View>
        <View className="flex-row items-center justify-between">
          <Text variant="small" className="text-primary/80">
            {formatWorkspaceMetric(event.rsvp_count)} going
          </Text>
          <Icon as={ArrowRightIcon} size={12} className="text-muted-foreground/40" />
        </View>
      </View>
    </Pressable>
  );
}

function ActivityRow({ item, first }: { item: OrgActivityItem; first?: boolean }) {
  const tone = activityTone(item.type);
  return (
    <View className={cn('flex-row items-start gap-3 py-4', !first && 'border-t border-border/50')}>
      <View className={tone.wrapClassName}>
        <Icon as={tone.icon} size={14} className={tone.iconClassName} />
      </View>
      <View className="flex-1 gap-0.5">
        <View className="flex-row flex-wrap items-baseline gap-x-2">
          <Text className="text-[13px] font-semibold text-foreground">
            {item.actor_display_name}
          </Text>
          <Text variant="small" className="text-muted-foreground/60">
            {formatRelativeTime(item.created_at)}
          </Text>
        </View>
        <Text className="text-[13px] text-foreground/80">{item.primary_text}</Text>
        {item.secondary_text ? (
          <Text variant="small" className="mt-0.5 leading-5 text-muted-foreground">
            {item.secondary_text}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function ActivityFilterChip({
  active,
  children,
  onPress,
}: {
  active: boolean;
  children: React.ReactNode;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      className={cn(
        'rounded-full border px-3.5 py-1.5',
        active ? 'border-primary/20 bg-primary/10' : 'border-border/80 bg-background'
      )}>
      <Text
        className={cn('text-[13px] font-semibold', active ? 'text-primary' : 'text-foreground')}>
        {children}
      </Text>
    </Pressable>
  );
}

function activityTone(type: OrgActivityEventType) {
  switch (type) {
    case 'organization_invite_sent':
    case 'organization_invite_accepted':
      return {
        icon: UserPlusIcon,
        wrapClassName:
          'mt-0.5 size-8 items-center justify-center rounded-full border border-primary/20 bg-primary/10',
        iconClassName: 'text-primary',
      };
    case 'organization_member_role_updated':
    case 'organization_member_scope_updated':
      return {
        icon: UserCogIcon,
        wrapClassName:
          'mt-0.5 size-8 items-center justify-center rounded-full border border-amber-500/20 bg-amber-500/10',
        iconClassName: 'text-amber-300',
      };
    case 'organization_member_removed':
    case 'organization_invite_revoked':
      return {
        icon: UserMinusIcon,
        wrapClassName:
          'mt-0.5 size-8 items-center justify-center rounded-full border border-destructive/20 bg-destructive/10',
        iconClassName: 'text-destructive',
      };
    case 'owner_handoff_started':
      return {
        icon: ShieldCheckIcon,
        wrapClassName:
          'mt-0.5 size-8 items-center justify-center rounded-full border border-primary/20 bg-primary/10',
        iconClassName: 'text-primary',
      };
    case 'event_boost_started':
      return {
        icon: SparklesIcon,
        wrapClassName:
          'mt-0.5 size-8 items-center justify-center rounded-full border border-orange-500/20 bg-orange-500/10',
        iconClassName: 'text-orange-400',
      };
    case 'owner_setup_completed':
    case 'venue_setup_completed':
    case 'venue_claim_approved':
      return {
        icon: BadgeCheckIcon,
        wrapClassName:
          'mt-0.5 size-8 items-center justify-center rounded-full border border-emerald-500/20 bg-emerald-500/10',
        iconClassName: 'text-emerald-400',
      };
    default:
      return {
        icon: Building2Icon,
        wrapClassName:
          'mt-0.5 size-8 items-center justify-center rounded-full border border-border bg-background',
        iconClassName: 'text-muted-foreground',
      };
  }
}
